<template>
  <div class="collab-crud">
    <!-- 页面头部：标题 + 操作按钮 -->
    <div v-if="title || canAdd || canDelete" class="collab-crud__header">
      <h2 v-if="title" class="collab-crud__title">{{ title }}</h2>
      <div class="collab-crud__actions">
        <a-space>
          <a-button
            v-if="canAdd"
            type="primary"
            @click="rowAdd()"
          >
            <template #icon><PlusOutlined /></template>
            {{ t('common.formAdd') }}
          </a-button>
          <a-button
            v-if="canDelete"
            danger
            :disabled="!selectedRows.length"
            @click="handleBatchDelete"
          >
            {{ t('common.batchDelete') }}
          </a-button>
        </a-space>
      </div>
    </div>

    <!-- 关键字搜索区 -->
    <a-card v-if="showSearch" class="collab-crud__search">
      <a-form layout="inline" @finish="onSearch">
        <a-form-item :label="t('common.keyword')">
          <a-input
            v-model:value="searchKeyWord"
            :placeholder="resolvedSearchPlaceholder"
            allow-clear
            style="width: 240px"
            @press-enter="onSearch"
          />
        </a-form-item>
        <a-form-item>
          <a-space>
            <a-button type="primary" html-type="submit" :loading="loading">
              {{ t('common.search') }}
            </a-button>
            <a-button @click="onReset">{{ t('common.reset') }}</a-button>
          </a-space>
        </a-form-item>
      </a-form>
    </a-card>

    <!-- 数据表格 -->
    <a-card class="collab-crud__table">
      <DataTable
        :columns="columns"
        :data-source="dataSource"
        :loading="loading"
        :pagination="pagination"
        :actions="tableActions"
        :row-key="rowKey"
        :row-selection="rowSelection"
        @change="onTableChange"
        @action="onTableAction"
      >
        <template v-for="(_, name) in $slots" #[name]="slotData">
          <slot :name="name" v-bind="slotData" />
        </template>
      </DataTable>
    </a-card>

    <!-- 新增/编辑表单弹窗 -->
    <FormModal
      v-model:visible="formVisible"
      :title="formMode === 'add' ? t('common.formAdd') : t('common.formEdit')"
      :ok-text="t('common.ok')"
      :cancel-text="t('common.cancel')"
      :model="formModel"
      :rules="formRules"
      :loading="formLoading"
      @submit="submitForm"
      @cancel="rowClose"
    >
      <slot name="form" :mode="formMode" :model="formModel" />
    </FormModal>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { Modal } from 'ant-design-vue'
import { EditOutlined, PlusOutlined } from '@ant-design/icons-vue'
import type { FormProps, TableColumnsType, TableProps } from 'ant-design-vue'
import type { Key } from 'ant-design-vue/es/table/interface'
import { DataTable, FormModal, type TableAction } from '@/shared/components'
import { dict } from '../config'
import { useCollabCrud } from '../composables/useCollabCrud'
import type { CollabPermissionConfig, CollabService } from '../types/crud'

interface Props {
  /** CollabCore 六接口服务（接受任意实体类型的 Service 实现） */
  service: CollabService
  /** 表格列配置 */
  columns: TableColumnsType
  /** 页面标题 */
  title?: string
  /** 权限配置 */
  permission?: CollabPermissionConfig
  /** 表单校验规则 */
  formRules?: FormProps['rules']
  /** 行主键字段 */
  rowKey?: string
  /** 是否显示搜索区 */
  showSearch?: boolean
  /** 搜索框占位符 */
  searchPlaceholder?: string
  /** 是否在挂载时自动加载 */
  autoLoad?: boolean
  /** 新增表单默认值 */
  createDefaults?: Record<string, unknown>
}

const props = withDefaults(defineProps<Props>(), {
  rowKey: dict.primaryId,
  showSearch: true,
  autoLoad: true,
})

const { t } = useI18n()

const resolvedSearchPlaceholder = computed(
  () => props.searchPlaceholder ?? t('common.searchPlaceholderDefault'),
)

const crud = useCollabCrud({
  service: props.service,
  permission: props.permission,
  createDefaults: props.createDefaults,
})

const {
  loading,
  formLoading,
  dataSource,
  pagination,
  selectedRows,
  formVisible,
  formMode,
  formModel,
  canAdd,
  canUpdate,
  canDelete,
  rowAdd,
  rowClose,
  rowDelete,
  submitForm,
  handleTableChange,
  handleSearch,
  refresh,
} = crud

const searchKeyWord = ref('')

// 行选择配置（用于批量删除）
const rowSelection = computed<TableProps['rowSelection']>(() => {
  if (!canDelete.value) return undefined
  return {
    selectedRowKeys: selectedRows.value.map(
      (row) => (row as Record<string, unknown>)[props.rowKey] as Key,
    ),
    onChange: (_keys: Key[], rows: Record<string, unknown>[]) => {
      selectedRows.value = rows as typeof selectedRows.value
    },
  }
})

// 行内操作：编辑、删除
const tableActions = computed<TableAction[]>(() => {
  const actions: TableAction[] = []
  if (canUpdate.value) {
    actions.push({
      key: 'edit',
      label: t('common.edit'),
      type: 'link',
      icon: EditOutlined,
    })
  }
  if (canDelete.value) {
    actions.push({
      key: 'delete',
      label: t('common.delete'),
      type: 'danger',
      confirm: t('common.confirmDeleteOne'),
    })
  }
  return actions
})

/** 搜索提交 */
function onSearch() {
  handleSearch(searchKeyWord.value.trim())
}

/** 重置搜索 */
function onReset() {
  searchKeyWord.value = ''
  handleSearch('')
}

/** 表格分页/排序变化 */
function onTableChange(paginationInfo: unknown, filters: unknown, sorter: unknown) {
  handleTableChange(
    paginationInfo as { current?: number; pageSize?: number },
    filters,
    sorter as { field?: string; order?: string },
  )
}

/** 行内操作 */
function onTableAction(action: string, record: Record<string, unknown>) {
  if (action === 'edit') {
    crud.rowEdit(record)
  } else if (action === 'delete') {
    rowDelete([record])
  }
}

/** 批量删除确认 */
function handleBatchDelete() {
  Modal.confirm({
    title: t('common.confirmDeleteTitle'),
    content: t('common.confirmDeleteBatch', { count: selectedRows.value.length }),
    okText: t('common.ok'),
    cancelText: t('common.cancel'),
    onOk: () => rowDelete(selectedRows.value as Array<Record<string, unknown>>),
  })
}

onMounted(() => {
  if (props.autoLoad) {
    refresh()
  }
})

// 暴露 crud 实例供父组件扩展
defineExpose({ crud })
</script>

<style scoped lang="less">
.collab-crud {
  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 16px;
  }

  &__title {
    margin: 0;
    font-size: 20px;
    font-weight: 600;
  }

  &__search {
    margin-bottom: 16px;
  }

  &__table {
    :deep(.ant-card-body) {
      padding: 16px;
    }
  }
}
</style>
